import React from "react";
import {Badge, Table, Tag} from "antd";

import {CheckOutlined, CloseOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {TableContainer} from "./ResponsiveDataTable.styles";
import {courierStatusColors, courierStatuses} from "../../../constants/constants";
import {getConstant, isConstantExist} from "../../../utils/helpers";
import moment from "moment";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {useStore} from "../../../hooks/useStore";
import {EmptyMessage} from "../../_defaultComponents/EmptyMessage/EmptyMessage";

const TABLE_PREFIX = "availableCouriersTable";

const ResponsiveDataTable = ({data = [], loading, currentPage, setCurrentPage, dataCount}) => {
    const {t} = useTranslation([]);
    const stores = useStore();

    const statusFilters = [
        { text: t(getConstant(courierStatuses, "available").translation_key, { ns: "courierStatuses" }), value: ["available", "ready"] },
        { text: t(getConstant(courierStatuses, "assigned").translation_key, { ns: "courierStatuses" }), value: ["assigned"] },
        { text: t(getConstant(courierStatuses, "waitingForCourier").translation_key, { ns: "courierStatuses" }), value: ["waitingForCourier"] },
        { text: t(getConstant(courierStatuses, "busy").translation_key, { ns: "courierStatuses" }), value: ["busy"] },
        { text: t(getConstant(courierStatuses, "unavailable").translation_key, { ns: "courierStatuses" }), value: ["unavailable"] }
    ];
    const filterDropdown = (props) => {
        const { setSelectedKeys, selectedKeys, confirm, clearFilters, filters } = props;
        return (
            <div style={{ padding: 8 }}>
                <div
                    key="reset"
                    style={{ 
                        cursor: 'pointer', 
                        padding: '4px 8px',
                        borderBottom: '1px solid #f0f0f0',
                        marginBottom: '8px',
                        color: '#1890ff'
                    }}
                    onClick={() => {
                        clearFilters();
                        confirm();
                    }}
                >
                    Tümü
                </div>
                {filters.map(filter => (
                    <div
                        key={String(filter.value)}
                        style={{ 
                            cursor: 'pointer', 
                            padding: '4px 8px',
                            backgroundColor: selectedKeys.includes(filter.value) ? '#e6f7ff' : 'transparent'
                        }}
                        onClick={() => {
                            setSelectedKeys([filter.value]);
                            confirm();
                        }}
                    >
                        {filter.text}
                    </div>
                ))}
            </div>
        );
    };
    return (
        <>
            <TableContainer
                className={TABLE_PREFIX}
                pagination={
                    false /* {
                    total: dataCount,
                    current: currentPage,
                    onChange: async page => {
                        setCurrentPage(page);
                    },
                    hideOnSinglePage: true,
                    showSizeChanger: false
                } */
                }
                rowKey="id"
                rowClassName="hello"
                dataSource={data}
                data-testid="availableCourierDataTable"
                size="small"
                scroll={{x: "max-content"}}
                style={{width: "100%"}}
                loading={loading}
                locale={{
                    emptyText: <EmptyMessage />
                }}
            >
                <Table.Column
                    title="Kurye Adı"
                    dataIndex="name"
                    key="name"
                    sorter={(a, b) => a.name.localeCompare(b.name, "tr")}
                    render={(name, record) => (
                        <a
                            onClick={() => window.open(`/couriers/${record.id}`, "_blank")}
                            style={{ cursor: 'pointer' }}
                        >
                            <b>{name}</b>
                        </a>
                    )}
                />
                <Table.Column
                    title={<span style={{ fontWeight: 'bold' }}>Atanabilir Mi?</span>}
                    dataIndex="isEligible"
                    key="isEligible"
                    filters={[
                        { text: 'Atanabilir', value: true },
                        { text: 'Atanamaz', value: false }
                    ]}
                    filterMultiple={false}
                    onFilter={(value, record) => record.isEligible === value}
                    filterDropdown={filterDropdown}
                    render={isEligible => (
                        <span style={{ 
                            color: isEligible ? '#52c41a' : '#ff4d4f',
                            display: 'flex',
                            justifyContent: 'center',
                            fontSize: '20px'
                        }}>
                            {isEligible ? 
                                <CheckOutlined style={{ strokeWidth: 50 }} className="anticon-check" /> : 
                                <CloseOutlined style={{ strokeWidth: 50 }} className="anticon-close" />
                            }
                        </span>
                    )}
                />
                <Table.Column
                    title="Kuryenin Vardiyası Var Mı?"
                    dataIndex="hasShift"
                    key="hasShift"
                    filters={[
                        { text: 'Var', value: true },
                        { text: 'Yok', value: false }
                    ]}
                    filterMultiple={false}
                    onFilter={(value, record) => record.hasShift === value}
                    filterDropdown={filterDropdown}
                    render={hasShift => (
                        <Tag color={hasShift ? "green" : "red"}>
                            {hasShift ? "Evet" : "Hayır"}
                        </Tag>
                    )}
                />
                <Table.Column
                    title="Durum"
                    dataIndex="status"
                    key="status"
                    filters={statusFilters}
                    onFilter={(value, record) => value.includes(record.status)}
                    filterDropdown={filterDropdown}
                    defaultSortOrder={"ascend"}
                    render={status => (
                        <Tag data-testid="courierStatus">
                            <Badge
                                data-testid="courierStatus_badge"
                                data-testvalue={Boolean(status)}
                                color={courierStatusColors[status]}
                            />
                            {isConstantExist(courierStatuses, status)
                                ? t(getConstant(courierStatuses, status).translation_key, {
                                      ns: "courierStatuses"
                                  })
                                : "-"}
                        </Tag>
                    )}
                />
                <Table.Column
                    title="Kurye Üstünde Zorunlu Sipariş Var Mı?"
                    dataIndex="isForced"
                    key="isForced"
                    filters={[
                        { text: 'Var', value: true },
                        { text: 'Yok', value: false }
                    ]}
                    filterMultiple={false}
                    onFilter={(value, record) => record.isForced === value}
                    filterDropdown={filterDropdown}
                    render={isForced => (
                        <Tag color={isForced ? "green" : "red"}>
                            {isForced ? "Evet" : "Hayır"}
                        </Tag>
                    )}
                />
                <Table.Column
                    title="Kurye Maksimum Uzaklık Sınırı İçinde Mi?"
                    dataIndex="isFar"
                    key="isFar"
                    filters={[
                        { text: 'İçinde', value: true },
                        { text: 'Dışında', value: false }
                    ]}
                    filterMultiple={false}
                    onFilter={(value, record) => record.isFar !== value}
                    filterDropdown={filterDropdown}
                    render={isFar => (
                        <Tag color={isFar ? "red" : "green"}>
                            {isFar ? "Hayır" : "Evet"}
                        </Tag>
                    )}
                />
                <Table.Column
                    title="Uzaklık"
                    dataIndex="distance"
                    key="distance"
                    sorter={(a, b) => a?.distance - b?.distance}
                    render={distance => <>{distance?.toFixed(1)} km</>}
                />
                <Table.Column
                    title="Son Konum Tarihi"
                    dataIndex="lastLocationTime"
                    key="lastLocationTime"
                    sorter={(a, b) => moment(a.lastLocationTime).diff(moment(b.lastLocationTime))}
                    render={lastLocationTime => (
                        <>{moment(lastLocationTime).format("DD.MM.YYYY HH:mm")}</>
                    )}
                />
            </TableContainer>
        </>
    );
};

export default ResponsiveDataTable;
